import { bindable } from 'aurelia-typed-observable-plugin';
import { bindingMode, autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { ProviderService } from 'app/cd/services/provider-service';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { AuthContext } from 'app/um/lib/auth-context';
import { guid } from 'app/shared/lib/guid';
import { deepClone } from 'app/shared/lib/utilities';
import { localSort } from 'app/shared/lib/sorting';
import { AuthProviderReceivingReloadEvent } from 'app/um/lib/events';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';

interface IContact extends CD.ProviderContact {
    isPreferred?: boolean;
    isProvider?: boolean
    provider?: CD.Provider
}

@autoinject()
export class ExistingContact extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public contact: CD.ProviderContact | null = null;

    @bindable.object({ changeHandler: 'reload' })
    protected requestingProvider: UM.AuthProviderModel | null = null;

    @bindable.boolean()
    protected readonly: boolean = true;

    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;
    protected auth: UM.Auth | null = null;
    protected contacts: IContact[] = [];
    protected contactsToDisplay: IContact[] = [];
    public contactSaved: boolean = false;

    protected sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 5;

    protected guid: guid = guid();
    private subscriptions: Array<Subscription> = [];

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly context: AuthContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);

        this.checkPermissions();
    }

    protected bind(): void {
        this.reload();

        this.subscriptions.push(
            this.events.subscribe(AuthProviderReceivingReloadEvent, (e: AuthProviderReceivingReloadEvent) => this.handleAuthProviderReceivingReloadEvent(e))
        );
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        this.auth = this.context.auth;
        this.contactSaved = !!this.auth?.preferredContact?.id;
        await this.loadContacts();
         this.setDefaultSelection();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.getContactsToDisplay();
    }

    public setPreferred(contact: IContact) {
        const tempContacts = this.contactsToDisplay.map(c => {
            c.id === contact.id ? c.isPreferred = true : c.isPreferred = false;
            return c;
        });
        this.contactsToDisplay = deepClone(tempContacts);
        this.contact = contact;
    }

    protected getRequestingProviderContact(): IContact | void {
        if(!this.requestingProvider) return;
        const providerContact: IContact = {
            $display: this.requestingProvider?.location?.$display,
            emailAddress: this.requestingProvider?.emailAddress,
            phoneNumber: this.requestingProvider?.location?.facility?.phoneNumber,
            name: this.requestingProvider?.name,
            isPreferred: false,
            isProvider: true,
            provider: this.requestingProvider,
        };

        return providerContact;
    }

    public reset() {
        const tempContacts = this.contacts.map(c => {
            c.isPreferred = false;
            return c;
        });
        this.contacts = deepClone(tempContacts);
        this.contact = null;
        this.getContactsToDisplay();
    }

    private getContactsToDisplay() {
        // Apply filter
        this.contactsToDisplay = [...this.contacts];
        const defaultSort = new Sorting();
        defaultSort.add({
            reference: 'name.full',
            direction: SortDirection.Ascending
        });
        localSort(this.contactsToDisplay, this.sorting.entries.length > 0 ? this.sorting : defaultSort);
        const requestingProvider = this.getRequestingProviderContact();
        // only slot in requesting provider if they have both a name and a fax number
        if(requestingProvider && requestingProvider.name && requestingProvider.phoneNumber?.fax) {
            this.contactsToDisplay = [requestingProvider, ...this.contactsToDisplay];
            // if there is no preferred contact already selected, default selection to requesting provider
            if(!this.contactSaved && !this.contact?.name) this.setPreferred(requestingProvider);
        }
        const currentPage = this.$pager?.page || 0;
        this.resultCount = this.contactsToDisplay.length;
        this.contactsToDisplay = this.contactsToDisplay.slice(currentPage * this.pageSize, currentPage * this.pageSize + this.pageSize);

        // if there is a contact selected, reselect it when paging, sorting, etc.
        if(this.contact?.name) this.setPreferred(this.contact);
    }

    protected async handleAuthProviderReceivingReloadEvent(e: AuthProviderReceivingReloadEvent) {
        await this.context.refreshAuthSettings();
        this.requestingProvider = this.context.auth?.provider?.requesting || null;
    }

    // Set default contact selection to first choice
    protected setDefaultSelection(): void {
        if(this.contactsToDisplay.length) {
            this.setPreferred(this.contactsToDisplay[0]);
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadContacts(): Promise<void> {
        if(!this.requestingProvider?.location?.id) return;
        const options: StandardLoadOptions = {
            all: true,
            includeInactive: false
        };

        const includes: string[] = [
            'location',
            'phoneNumber.primary',
            'phoneNumber.fax'
        ];

        try {
            this.state.beginLoad();
            const response = await this.providerService.loadProviderContactsWithFaxByLocation(this.requestingProvider.location.id, options, includes);
            this.contacts = response.results;
            this.getContactsToDisplay();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
